import { useCallback, useEffect, useState } from "react";

import { logError } from "utility/Function";
import {
  platformGet,
  platformGetBlob,
  platformPut,
  PlatformRestError,
} from "./platform";

export const DASHBOARD_REPORTS_PATH = "/api/v1/dashboard/reports/";

export type ClientReport = {
  id: number;
  quarter: number;
  year: number;
  title: string;
  size_bytes: number;
  uploaded_at: string;
};

export type DashboardReports =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; reports: ClientReport[] };

// An expired session, or web running ahead of Platform.
const UNREMARKABLE_STATUSES = [401, 404];

/**
 * The company's live reports, newest period first as Platform orders them.
 * Fetched when the tab opens; `retry` asks again after a failure.
 */
export function useDashboardReports(): {
  reports: DashboardReports;
  retry: () => void;
} {
  const [reports, setReports] = useState<DashboardReports>({
    state: "loading",
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    platformGet<ClientReport[]>(DASHBOARD_REPORTS_PATH)
      .then((answer) => {
        if (!cancelled) setReports({ state: "ready", reports: answer });
      })
      .catch((error) => {
        if (cancelled) return;
        setReports({ state: "error" });
        const status =
          error instanceof PlatformRestError ? error.status : undefined;
        if (status === undefined || !UNREMARKABLE_STATUSES.includes(status)) {
          logError("Company dashboard reports failed", error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => {
    setReports({ state: "loading" });
    setAttempt((n) => n + 1);
  }, []);

  return { reports, retry };
}

export const REPORT_EMAIL_PREFERENCE_PATH = `${DASHBOARD_REPORTS_PATH}email-preference/`;

type ReportEmailAnswer = { enabled: boolean };

const enabledFrom = (answer: ReportEmailAnswer): boolean => {
  if (typeof answer?.enabled !== "boolean") {
    throw new Error(
      `Report email preference answer has no boolean enabled: ${JSON.stringify(
        answer
      )}`
    );
  }
  return answer.enabled;
};

export type ReportEmailPreference =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; enabled: boolean; saving: boolean; saveFailed: boolean };

/**
 * Whether this admin is emailed when a report is published — theirs alone,
 * never a colleague's. A change shows at once and is rolled back, with
 * `saveFailed` set, if Platform refuses it; `onSaved` hears only a change
 * Platform kept.
 */
export function useReportEmailPreference(
  onSaved?: (enabled: boolean) => void
): {
  preference: ReportEmailPreference;
  setEnabled: (enabled: boolean) => void;
  retry: () => void;
} {
  const [preference, setPreference] = useState<ReportEmailPreference>({
    state: "loading",
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    platformGet<ReportEmailAnswer>(REPORT_EMAIL_PREFERENCE_PATH)
      .then((answer) => {
        if (cancelled) return;
        setPreference({
          state: "ready",
          enabled: enabledFrom(answer),
          saving: false,
          saveFailed: false,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setPreference({ state: "error" });
        const status =
          error instanceof PlatformRestError ? error.status : undefined;
        if (status === undefined || !UNREMARKABLE_STATUSES.includes(status)) {
          logError("Company dashboard report email preference failed", error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const setEnabled = useCallback(
    (enabled: boolean) => {
      setPreference({
        state: "ready",
        enabled,
        saving: true,
        saveFailed: false,
      });
      // Two-argument `then`, so a throw from `onSaved` is not mistaken for a
      // refused save and rolled back.
      platformPut<ReportEmailAnswer>(REPORT_EMAIL_PREFERENCE_PATH, { enabled })
        .then(enabledFrom)
        .then(
          (kept) => {
            setPreference({
              state: "ready",
              enabled: kept,
              saving: false,
              saveFailed: false,
            });
            onSaved?.(kept);
          },
          (error) => {
            setPreference({
              state: "ready",
              enabled: !enabled,
              saving: false,
              saveFailed: true,
            });
            const status =
              error instanceof PlatformRestError ? error.status : undefined;
            if (
              status === undefined ||
              !UNREMARKABLE_STATUSES.includes(status)
            ) {
              logError(
                "Company dashboard report email preference save failed",
                error
              );
            }
          }
        );
    },
    [onSaved]
  );

  const retry = useCallback(() => {
    setPreference({ state: "loading" });
    setAttempt((n) => n + 1);
  }, []);

  return { preference, setEnabled, retry };
}

const DOWNLOAD_REVOKE_DELAY_MS = 30_000;

export const reportFilePath = (id: number, download = false) =>
  `${DASHBOARD_REPORTS_PATH}${id}/file/${download ? "?download=1" : ""}`;

export type ReportFile =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; url: string };

/**
 * The selected report's PDF as an object URL the browser's own viewer can
 * show — the file endpoint needs the bearer, so a plain link cannot. The URL
 * is revoked when the selection changes or the viewer goes away, and an
 * answer for a report that is no longer selected is dropped.
 */
export function useReportFile(id: number): {
  file: ReportFile;
  retry: () => void;
} {
  const [file, setFile] = useState<ReportFile>({ state: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setFile({ state: "loading" });
    platformGetBlob(reportFilePath(id))
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setFile({ state: "ready", url });
      })
      .catch((error) => {
        if (cancelled) return;
        setFile({ state: "error" });
        const status =
          error instanceof PlatformRestError ? error.status : undefined;
        if (status === undefined || !UNREMARKABLE_STATUSES.includes(status)) {
          logError("Company dashboard report file failed", error);
        }
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { file, retry };
}

/** Saves the report to disk through the same authenticated path. */
export async function downloadReportFile(
  id: number,
  filename: string
): Promise<void> {
  const blob = await platformGetBlob(reportFilePath(id, true));
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Some Safari versions start the save after click() returns.
  setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_REVOKE_DELAY_MS);
}
